import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { isEpisodeCard, groupEpisodes } from '../lib/longformGroups';
import { cardTitle } from '../lib/cardTitle';

const STATUS_ORDER = ['Filmed', 'Editing', 'Qual'];


const STATUS_COLOR = {
  Filmed:  'var(--s-filmed-t)',
  Editing: 'var(--s-editing-t)',
  Qual:    'var(--s-qual-t)',
};

function formatDate(str) {
  if (!str) return null;
  const [y, m, d] = str.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}


const MONTH_NAMES = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December'];
const DAY_NAMES   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function dateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function buildCalDays(year, month) {
  const first = new Date(year, month, 1);
  const last  = new Date(year, month + 1, 0);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  const end = new Date(last);
  end.setDate(last.getDate() + (6 - last.getDay()));
  const days = [];
  const cur  = new Date(start);
  while (cur <= end) { days.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
  return days;
}

function buildWeekDays(anchor) {
  const d = new Date(anchor);
  d.setDate(d.getDate() - d.getDay());
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(d);
    day.setDate(d.getDate() + i);
    return day;
  });
}

const CAL_MAX_CHIPS = 3;

// ── Main page ─────────────────────────────────────────────────────────────────

export default function EditQueue() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [records,      setRecords]      = useState([]);
  const [members,      setMembers]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [view,         setView]         = useState('queue'); // 'queue' | 'calendar'
  const [queueSort,    setQueueSort]    = useState('editor'); // 'editor' | 'project'
  const [editorFilter, setEditorFilter] = useState('all');
  const [videoCard,    setVideoCard]    = useState(null);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/live-production/edit-calendar');
      setRecords(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);
  useEffect(() => {
    api.get('/api/team-members').then(r => setMembers(r.data)).catch(() => {});
  }, []);

  const editors = useMemo(
    () => members.filter(m => m.roles?.some(r => r.role === 'Editor')),
    [members]
  );

  const filtered = useMemo(() => {
    if (editorFilter === 'all') return records;
    return records.filter(r => r.editorId === editorFilter);
  }, [records, editorFilter]);

  function handleRecordUpdated(updated) {
    setRecords(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated } : r));
    setVideoCard(prev => prev?.id === updated.id ? { ...prev, ...updated } : prev);
  }

  function handleRecordStatusChanged(id, newStatus) {
    setRecords(prev => prev.map(r => r.id === id ? { ...r, status: newStatus } : r));
  }

  // P-1: the Video Card is a page now.
  function openVideoCard(record) {
    navigate(`/production/${record.id}`);
  }

  const unscheduled = useMemo(
    () => filtered.filter(r => !r.editScheduledDate),
    [filtered]
  );

  async function handleEditDateChange(record, dateStr) {
    // Optimistic update — move chip immediately
    setRecords(prev => prev.map(r => r.id === record.id ? { ...r, editScheduledDate: dateStr || null } : r));
    try {
      const res = await api.patch(`/api/live-production/${record.id}`, {
        editScheduledDate: dateStr || null,
      });
      handleRecordUpdated(res.data);
    } catch {
      // Revert on error — never call fetchRecords here (would unmount CalendarView, resetting view to 'week')
      setRecords(prev => prev.map(r => r.id === record.id ? { ...r, editScheduledDate: record.editScheduledDate ?? null } : r));
    }
  }

  return (
    <div className="main">
      <div className="topbar">
        <div>
          <div className="page-title">Edit Queue</div>
          <div className="page-sub">{filtered.length} video{filtered.length !== 1 ? 's' : ''} in edit workflow</div>
        </div>
        <div className="topbar-actions">
          <select
            className="filter-input"
            style={{ width: 180 }}
            value={editorFilter}
            onChange={e => setEditorFilter(e.target.value)}
          >
            <option value="all">All Editors</option>
            {editors.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>

          {view === 'queue' && (
            <div style={{ display: 'flex', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', overflow: 'hidden' }}>
              {[['editor', 'By Editor'], ['project', 'By Project']].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setQueueSort(val)}
                  style={{
                    padding: '7px 14px', fontSize: 12, fontFamily: 'var(--font)', cursor: 'pointer', border: 'none',
                    background: queueSort === val ? 'var(--violet)' : 'transparent',
                    color: queueSort === val ? '#fff' : 'var(--tx-secondary)',
                    fontWeight: queueSort === val ? 600 : 400,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', overflow: 'hidden' }}>
            {['queue', 'calendar'].map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                style={{
                  padding: '7px 16px', fontSize: 12, fontFamily: 'var(--font)', cursor: 'pointer', border: 'none',
                  background: view === v ? 'var(--violet)' : 'transparent',
                  color: view === v ? '#fff' : 'var(--tx-secondary)',
                  fontWeight: view === v ? 600 : 400,
                }}
              >
                {v === 'queue' ? 'Queue' : 'Calendar'}
              </button>
            ))}
          </div>

          <button className="btn-ghost" onClick={fetchRecords}>↺ Refresh</button>
        </div>
      </div>

      <div className="body" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {loading ? (
          <div style={{ color: 'var(--tx-faint)', fontSize: 13, padding: 40, textAlign: 'center' }}>Loading…</div>
        ) : view === 'queue' ? (
          <QueueView
            records={filtered}
            editors={editors}
            groupBy={queueSort}
            userId={user?.id}
            onUpdated={handleRecordUpdated}
            onStatusChanged={handleRecordStatusChanged}
            onOpenVideoCard={openVideoCard}
          />
        ) : (
          <CalendarView
            records={filtered}
            unscheduled={unscheduled}
            onDateChange={handleEditDateChange}
            onOpenVideoCard={openVideoCard}
          />
        )}
      </div>

    </div>
  );
}

// ── Queue view ────────────────────────────────────────────────────────────────

function QueueView({ records, editors, groupBy, userId, onUpdated, onStatusChanged, onOpenVideoCard }) {
  const grouped = useMemo(() => {
    const map = {};
    if (groupBy === 'project') {
      records.forEach(r => {
        const key  = r.clientId || '__unknown';
        const name = r.client?.name || 'Unknown Project';
        if (!map[key]) map[key] = { groupKey: key, name, records: [] };
        map[key].records.push(r);
      });
      return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
    }
    // Default: group by editor
    records.forEach(r => {
      const key  = r.editorId  || '__unassigned';
      const name = r.editor?.name || 'Unassigned';
      if (!map[key]) map[key] = { groupKey: key, name, records: [] };
      map[key].records.push(r);
    });
    // Sort: assigned editors first (alphabetical), unassigned last
    return Object.values(map).sort((a, b) => {
      if (a.groupKey === '__unassigned') return 1;
      if (b.groupKey === '__unassigned') return -1;
      return a.name.localeCompare(b.name);
    });
  }, [records, groupBy]);

  if (records.length === 0) {
    return (
      <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--tx-faint)', fontSize: 13 }}>
        No videos in Filmed, Editing, or Qual status
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {grouped.map(group => (
        <div key={group.groupKey} className="eq-group">
          <div className="eq-group-header">
            <div className="eq-editor-avatar">{group.name.charAt(0).toUpperCase()}</div>
            <div>
              <div className="eq-editor-name">{group.name}</div>
              <div className="eq-editor-count">{group.records.length} video{group.records.length !== 1 ? 's' : ''}</div>
            </div>
          </div>
          <div className="eq-records">
            {/* D-120: status-ordered as before; episode cards of one series collapse under a series sub-header */}
            {groupEpisodes(STATUS_ORDER.flatMap(status => group.records.filter(r => r.status === status))).flatMap(entry =>
              entry.kind === 'series'
                ? [
                    <div key={entry.key} className="eq-series-header" data-testid="eq-series">
                      <span className="eq-series-name">{entry.scriptName}</span>
                      <span className="eq-series-meta">{entry.episodes.length} episode{entry.episodes.length !== 1 ? 's' : ''} · {entry.progress.label}</span>
                    </div>,
                    ...entry.episodes.map(r => (
                      <EqRecord
                        key={r.id}
                        record={r}
                        nested
                        userId={userId}
                        onUpdated={onUpdated}
                        onStatusChanged={onStatusChanged}
                        onOpenVideoCard={onOpenVideoCard}
                      />
                    )),
                  ]
                : [
                    <EqRecord
                      key={entry.record.id}
                      record={entry.record}
                      userId={userId}
                      onUpdated={onUpdated}
                      onStatusChanged={onStatusChanged}
                      onOpenVideoCard={onOpenVideoCard}
                    />,
                  ]
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Edit Queue record card ────────────────────────────────────────────────────

function EqRecord({ record, nested = false, userId, onUpdated, onStatusChanged, onOpenVideoCard }) {
  const [editNotes,   setEditNotes]   = useState(record.editNotes || '');
  const [notesDirty,  setNotesDirty]  = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [transitioning, setTransitioning] = useState(false);

  const displayName = isEpisodeCard(record)
    ? `Ep ${record.episodeNumber}${record.episodeTitle ? ` — ${record.episodeTitle}` : ''}`
    : (record.script?.scriptName || record.notes || '(Riff)');
  const editDate    = record.editScheduledDate ? formatDate(record.editScheduledDate) : null;

  async function saveNotes() {
    if (!notesDirty) return;
    setSavingNotes(true);
    try {
      const res = await api.patch(`/api/live-production/${record.id}`, { editNotes: editNotes || null });
      onUpdated(res.data);
      setNotesDirty(false);
    } finally {
      setSavingNotes(false);
    }
  }

  async function handleMarkQual() {
    setTransitioning(true);
    try {
      const res = await api.patch(`/api/live-production/${record.id}/status`, {
        status: 'Qual', changedBy: userId,
      });
      onUpdated(res.data);
      onStatusChanged(record.id, 'Qual');
    } catch (err) {
      alert(err.response?.data?.error || 'Status change failed');
    } finally {
      setTransitioning(false);
    }
  }

  async function handleStartEditing() {
    setTransitioning(true);
    try {
      const res = await api.patch(`/api/live-production/${record.id}/status`, {
        status: 'Editing', changedBy: userId,
      });
      onUpdated(res.data);
      onStatusChanged(record.id, 'Editing');
    } catch (err) {
      alert(err.response?.data?.error || 'Status change failed');
    } finally {
      setTransitioning(false);
    }
  }

  return (
    <div className={`eq-record${nested ? ' nested' : ''}`}>
      <div className="eq-record-top">
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="eq-record-title">{displayName}</div>
          <div className="eq-record-meta">
            <span style={{ color: STATUS_COLOR[record.status] || 'var(--tx-muted)', fontWeight: 600 }}>
              {record.status}
            </span>
            <span style={{ color: 'var(--tx-faint)' }}>·</span>
            <span>{record.client?.name || '—'}</span>
            {editDate && (
              <>
                <span style={{ color: 'var(--tx-faint)' }}>·</span>
                <span style={{ color: 'var(--violet)' }}>Edit: {editDate}</span>
              </>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
          {record.status === 'Filmed' && (
            <button className="btn-ghost" style={{ fontSize: 11 }} onClick={handleStartEditing} disabled={transitioning}>
              Start Editing →
            </button>
          )}
          {record.status === 'Editing' && (
            <button
              className="btn-primary"
              style={{ fontSize: 11, padding: '6px 14px' }}
              onClick={handleMarkQual}
              disabled={transitioning}
            >
              Ready for Qual ✓
            </button>
          )}
          <button
            className="btn-ghost"
            style={{ fontSize: 11 }}
            onClick={() => onOpenVideoCard(record)}
          >
            Open production ↗
          </button>
        </div>
      </div>

      <div className="eq-notes-row">
        <textarea
          className="eq-notes-input"
          placeholder="Handoff notes for editor…"
          value={editNotes}
          onChange={e => { setEditNotes(e.target.value); setNotesDirty(true); }}
          onBlur={saveNotes}
        />
        {notesDirty && (
          <button
            className="btn-ghost"
            style={{ fontSize: 11, flexShrink: 0, alignSelf: 'flex-end' }}
            onClick={saveNotes}
            disabled={savingNotes}
          >
            {savingNotes ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>

      {record.frameLink && (
        <div style={{ paddingTop: 4 }}>
          <a
            href={record.frameLink}
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: 11, color: 'var(--violet)', textDecoration: 'none', fontWeight: 600 }}
          >
            Frame Review ↗
          </a>
        </div>
      )}
    </div>
  );
}

// ── Calendar view ─────────────────────────────────────────────────────────────

function CalendarView({ records, unscheduled, onDateChange, onOpenVideoCard }) {
  const [calView,      setCalView]      = useState('week'); // 'week' | 'month'
  const [viewDate,     setViewDate]     = useState(new Date());
  const [dragOver,     setDragOver]     = useState(null);
  const [pickerDay,    setPickerDay]    = useState(null); // dateKey string
  const [pickerSearch, setPickerSearch] = useState('');
  const dropJustHappened               = useRef(false);

  const todayKey = dateKey(new Date());

  // Navigation
  function prev() {
    setViewDate(d => {
      const n = new Date(d);
      if (calView === 'week') n.setDate(n.getDate() - 7);
      else n.setMonth(n.getMonth() - 1);
      return n;
    });
  }
  function next() {
    setViewDate(d => {
      const n = new Date(d);
      if (calView === 'week') n.setDate(n.getDate() + 7);
      else n.setMonth(n.getMonth() + 1);
      return n;
    });
  }
  function goToday() { setViewDate(new Date()); }

  // Calendar days
  const weekDays = useMemo(() => buildWeekDays(viewDate), [viewDate]);
  const year  = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const calDays = useMemo(() => buildCalDays(year, month), [year, month]);

  const navLabel = calView === 'week'
    ? `${weekDays[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${weekDays[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    : `${MONTH_NAMES[month]} ${year}`;

  // Map records by date key
  const byDate = useMemo(() => {
    const map = {};
    for (const r of records) {
      if (!r.editScheduledDate) continue;
      const k = r.editScheduledDate.slice(0, 10);
      if (!map[k]) map[k] = [];
      map[k].push(r);
    }
    return map;
  }, [records]);

  function handleDragStart(e, record) {
    e.dataTransfer.setData('text/plain', record.id);
    e.dataTransfer.effectAllowed = 'move';
  }

  const filteredUnscheduled = useMemo(() => {
    if (!pickerSearch) return unscheduled;
    const q = pickerSearch.toLowerCase();
    return unscheduled.filter(r =>
      r.script?.scriptName?.toLowerCase().includes(q) ||
      r.client?.name?.toLowerCase().includes(q) ||
      r.editor?.name?.toLowerCase().includes(q)
    );
  }, [unscheduled, pickerSearch]);

  // Shared cell renderer
  function renderCell(day, key, otherMonth = false) {
    const chips    = byDate[key] || [];
    const visible  = chips.slice(0, CAL_MAX_CHIPS);
    const overflow = chips.length - CAL_MAX_CHIPS;
    const isToday  = key === todayKey;

    return (
      <div
        key={key}
        className={`eq-cal-cell${dragOver === key ? ' drag-over' : ''}${otherMonth ? ' eq-cal-other' : ''}`}
        onDragOver={e => { e.preventDefault(); setDragOver(key); }}
        onDragLeave={() => setDragOver(null)}
        onDrop={e => {
          e.preventDefault(); setDragOver(null);
          dropJustHappened.current = true;
          setTimeout(() => { dropJustHappened.current = false; }, 200);
          const id = e.dataTransfer.getData('text/plain');
          if (!id) return;
          const rec = records.find(r => r.id === id) || unscheduled.find(r => r.id === id);
          if (!rec) return;
          onDateChange(rec, key);
        }}
        onClick={() => { if (dropJustHappened.current) return; setPickerDay(key); setPickerSearch(''); }}
      >
        <div className={`eq-cal-daylabel${isToday ? ' today' : ''}`}>
          {calView === 'week' && <span>{DAY_NAMES[day.getDay()]}</span>}
          <span>{day.getDate()}</span>
        </div>
        {visible.map(r => (
          <div
            key={r.id}
            className="eq-chip"
            draggable
            onDragStart={e => { e.stopPropagation(); handleDragStart(e, r); }}
            onDragEnd={() => {}}
            onClick={e => { e.stopPropagation(); onOpenVideoCard(r); }}
            style={{ borderLeftColor: STATUS_COLOR[r.status] || 'var(--violet)', cursor: 'grab' }}
          >
            <span className="eq-chip-title">{cardTitle(r)}</span>
            <button
              className="eq-chip-remove"
              onClick={e => { e.stopPropagation(); onDateChange(r, null); }}
            >✕</button>
          </div>
        ))}
        {overflow > 0 && <div className="cal-overflow">+{overflow} more</div>}
      </div>
    );
  }

  return (
    <div>
      {/* Nav bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <button className="btn-ghost" style={{ padding: '5px 12px', fontSize: 13 }} onClick={prev}>‹</button>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-primary)', minWidth: 220, textAlign: 'center' }}>
          {navLabel}
        </span>
        <button className="btn-ghost" style={{ padding: '5px 12px', fontSize: 13 }} onClick={next}>›</button>
        <button className="btn-ghost" style={{ padding: '5px 10px', fontSize: 11 }} onClick={goToday}>Today</button>

        {/* Color legend */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 12, fontSize: 11, color: 'var(--tx-muted)' }}>
          {[['Filmed', 'var(--s-filmed-t)'], ['Editing', 'var(--s-editing-t)'], ['Qual', 'var(--s-qual-t)']].map(([label, color]) => (
            <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }} />
              {label}
            </span>
          ))}
        </div>

        {/* Week / Month toggle */}
        <div style={{ marginLeft: 'auto', display: 'flex', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', overflow: 'hidden' }}>
          {['week', 'month'].map(v => (
            <button
              key={v}
              onClick={() => setCalView(v)}
              style={{
                padding: '5px 14px', fontSize: 11, fontFamily: 'var(--font)', cursor: 'pointer', border: 'none',
                background: calView === v ? 'var(--violet)' : 'transparent',
                color: calView === v ? '#fff' : 'var(--tx-secondary)',
                fontWeight: calView === v ? 600 : 400, textTransform: 'capitalize',
              }}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      {/* Month view: day-of-week header row */}
      {calView === 'month' && (
        <div className="eq-cal-grid" style={{ marginBottom: 2 }}>
          {DAY_NAMES.map(d => (
            <div key={d} style={{ fontSize: 10, fontWeight: 600, color: 'var(--tx-muted)', textAlign: 'center', padding: '4px 0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {d}
            </div>
          ))}
        </div>
      )}

      {/* Grid */}
      <div className={`eq-cal-grid${calView === 'month' ? ' eq-cal-month' : ''}`}>
        {calView === 'week'
          ? weekDays.map(day => renderCell(day, dateKey(day)))
          : calDays.map(day => renderCell(day, dateKey(day), day.getMonth() !== month))
        }
      </div>

      {/* Unscheduled tray */}
      {unscheduled.length > 0 && (
        <div className="eq-unscheduled">
          <div className="eq-unscheduled-label">Unscheduled ({unscheduled.length})</div>
          <div className="eq-unscheduled-list">
            {unscheduled.map(r => (
              <div
                key={r.id}
                className="eq-chip eq-chip-flat"
                draggable
                onDragStart={e => { e.stopPropagation(); handleDragStart(e, r); }}
                onDragEnd={() => {}}
                style={{ borderLeftColor: STATUS_COLOR[r.status] || 'var(--violet)', cursor: 'grab' }}
              >
                <span className="eq-chip-title">
                  {cardTitle(r)}
                  <span style={{ marginLeft: 4, color: 'var(--tx-faint)', fontSize: 10 }}>· {r.client?.initials || ''}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Assign picker */}
      {pickerDay && (
        <div className="modal-overlay" onClick={() => setPickerDay(null)}>
          <div className="cal-assign-popup" onClick={e => e.stopPropagation()}>
            <div className="cal-assign-head">
              <span className="cal-assign-label">
                Assign to {new Date(pickerDay + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </span>
              <button className="vc-close" style={{ fontSize: 14 }} onClick={() => setPickerDay(null)}>✕</button>
            </div>
            <input
              className="filter-input"
              style={{ margin: '10px 14px 6px', width: 'calc(100% - 28px)' }}
              placeholder="Search videos…"
              value={pickerSearch}
              autoFocus
              onChange={e => setPickerSearch(e.target.value)}
            />
            <div className="cal-assign-list">
              {filteredUnscheduled.length === 0 && (
                <div style={{ padding: '20px 14px', color: 'var(--tx-faint)', fontSize: 12, textAlign: 'center' }}>
                  No unscheduled videos
                </div>
              )}
              {filteredUnscheduled.map(r => (
                <div
                  key={r.id}
                  className="cal-assign-item"
                  onClick={() => { onDateChange(r, pickerDay); setPickerDay(null); }}
                >
                  <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--tx-primary)' }}>
                    {cardTitle(r)}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--tx-muted)', marginTop: 2, display: 'flex', gap: 8 }}>
                    <span>{r.client?.name}</span>
                    {r.editor?.name && <span>· {r.editor.name}</span>}
                    <span style={{ color: STATUS_COLOR[r.status] || 'var(--tx-muted)' }}>· {r.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
